import type { FieldsetHTMLAttributes, HTMLAttributes, LabelHTMLAttributes, Ref } from "react";

import { cn } from "@/lib/utils";

export function FieldGroup({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  return (
    <div data-slot="field-group" className={cn("flex flex-col gap-4", className)} {...props} />
  );
}

interface FieldProps extends HTMLAttributes<HTMLDivElement> {
  orientation?: "vertical" | "horizontal";
}

export function Field({
  className,
  orientation = "vertical",
  ...props
}: FieldProps): React.JSX.Element {
  return (
    <div
      data-slot="field"
      className={cn(
        "group/field flex gap-2 data-[invalid=true]:text-destructive",
        orientation === "horizontal" ? "flex-row items-center justify-between" : "flex-col",
        className,
      )}
      {...props}
    />
  );
}

interface FieldLabelProps extends LabelHTMLAttributes<HTMLLabelElement> {
  htmlFor: string;
  ref?: Ref<HTMLLabelElement>;
}

export function FieldLabel({
  className,
  htmlFor,
  ref,
  ...props
}: FieldLabelProps): React.JSX.Element {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: FieldLabel requires htmlFor and forwards children through props.
    <label
      ref={ref}
      htmlFor={htmlFor}
      data-slot="field-label"
      className={cn("text-body-3 font-medium text-foreground", className)}
      {...props}
    />
  );
}

export function FieldDescription({
  className,
  ...props
}: HTMLAttributes<HTMLParagraphElement>): React.JSX.Element {
  return (
    <p
      data-slot="field-description"
      className={cn(
        "text-body-3 text-muted-foreground group-data-[invalid=true]/field:text-destructive",
        className,
      )}
      {...props}
    />
  );
}

export function FieldSet({
  className,
  ...props
}: FieldsetHTMLAttributes<HTMLFieldSetElement>): React.JSX.Element {
  return (
    <fieldset data-slot="field-set" className={cn("flex flex-col gap-3", className)} {...props} />
  );
}

export function FieldLegend({
  className,
  ...props
}: HTMLAttributes<HTMLLegendElement>): React.JSX.Element {
  return (
    <legend
      data-slot="field-legend"
      className={cn("text-body-3 font-medium text-foreground", className)}
      {...props}
    />
  );
}
